import { useState } from "react";
import { MdAdd, MdNotificationsActive } from "react-icons/md";

import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Progress,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useAiBudgets } from "hooks/useAiBudgets";
import { useTranslation } from "hooks/useTranslation";
import {
  AiBudget,
  AiBudgetPeriodValue,
  aiGatewayService,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import BudgetModal from "./BudgetModal";

interface BudgetsPanelProps {
  /** Bumped by the page's Refresh button. Spend moves without an edit, so this matters here. */
  refreshToken: number;
}

const PERIOD_KEYS: Record<AiBudgetPeriodValue, string> = {
  [AiBudgetPeriodValue.Monthly]: "monthly",
  [AiBudgetPeriodValue.Weekly]: "weekly",
  [AiBudgetPeriodValue.Daily]: "daily",
};

const formatUsd = (value: number) =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

/** Nothing caps spend at the budget, so `percentUsed` can exceed 100 — the bar is clamped, not the number. */
const spendPalette = (percentUsed: number) => {
  if (percentUsed >= 100) return "red";
  if (percentUsed >= 80) return "orange";
  return "blue";
};

/**
 * Budget alerts — a spend threshold that sends a notification, not a limit that stops a call.
 *
 * That distinction is the whole feature and the panel says so out loud: crossing 100% of a budget
 * never fails a chat turn. Budgets measure tenant-plane, non-demo spend, so platform overhead and
 * demo traffic are invisible here by design.
 *
 * Evaluation is debounced, which means `currentSpendUsd` can move a little ahead of the alerts —
 * a threshold crossed seconds ago has not necessarily fired yet.
 */
const BudgetsPanel = ({ refreshToken }: BudgetsPanelProps) => {
  const { t } = useTranslation();
  const { budgets, alerts, widgets, customerCompanies, loading, reload } =
    useAiBudgets(refreshToken);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<AiBudget | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (budget: AiBudget) => {
    setEditing(budget);
    setIsModalOpen(true);
  };

  const handleDelete = async (budget: AiBudget) => {
    setDeletingId(budget.id);
    try {
      await aiGatewayService.deleteBudget(budget.id);
      reload();
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * `widgetName` is null for a workspace-wide budget *and* for one whose widget has since been
   * deleted. `widgetId` is what tells them apart.
   */
  const scopeLabel = (budget: AiBudget) => {
    if (budget.customerCompanyId)
      return t("app.aiGateway.budgets.scope.customerCompany", {
        name:
          budget.customerCompanyName ??
          t("app.aiGateway.budgets.scope.deletedCustomerCompany"),
      });
    if (!budget.widgetId) return t("app.aiGateway.budgets.scope.workspace");
    return budget.widgetName ?? t("app.aiGateway.budgets.scope.deletedWidget");
  };

  return (
    <VStack align="stretch" gap={5}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text
          fontSize="sm"
          color={getThemeColor("text.secondary")}
          maxW="560px"
        >
          {t("app.aiGateway.budgets.subtitle")}
        </Text>
        <Button intent="primary" size="sm" onClick={openCreate}>
          <MdAdd />
          {t("app.aiGateway.budgets.add")}
        </Button>
      </Flex>

      {!loading && budgets.length === 0 && (
        <VStack gap={3} py={10}>
          <Box color={getThemeColor("text.secondary")} fontSize="3xl">
            <MdNotificationsActive />
          </Box>
          <Text color={getThemeColor("text.secondary")} textAlign="center">
            {t("app.aiGateway.budgets.empty")}
          </Text>
          <Button intent="primary" size="sm" onClick={openCreate}>
            <MdAdd />
            {t("app.aiGateway.budgets.add")}
          </Button>
        </VStack>
      )}

      {budgets.length > 0 && (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="980px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.budgets.table.name")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.budgets.table.scope")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.budgets.table.period")}
                </Table.ColumnHeader>
                <Table.ColumnHeader minW="220px">
                  {t("app.aiGateway.budgets.table.spend")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.budgets.table.thresholds")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.budgets.table.status")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.budgets.table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {budgets.map((budget) => (
                <Table.Row key={budget.id}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="medium">{budget.name}</Text>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {formatUsd(budget.amountUsd)}
                      </Text>
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm">{scopeLabel(budget)}</Text>
                      {budget.enforceLimit && (
                        <Badge colorPalette="purple" size="sm">
                          {t("app.aiGateway.budgets.enforcedBadge")}
                        </Badge>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    {t(
                      `app.aiGateway.budgets.periods.${PERIOD_KEYS[budget.period]}`,
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <VStack align="stretch" gap={1}>
                      <Progress
                        value={Math.min(budget.percentUsed, 100)}
                        colorPalette={spendPalette(budget.percentUsed)}
                        size="sm"
                      />
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {t("app.aiGateway.budgets.table.spendValue", {
                          spend: formatUsd(budget.currentSpendUsd),
                          percent: budget.percentUsed.toFixed(0),
                        })}
                      </Text>
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={1} wrap="wrap">
                      {budget.thresholds.map((threshold) => (
                        <Badge
                          key={threshold}
                          size="sm"
                          colorPalette={
                            budget.triggeredThresholds.includes(threshold)
                              ? "red"
                              : "gray"
                          }
                        >
                          {threshold}%
                        </Badge>
                      ))}
                    </HStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      colorPalette={budget.isActive ? "green" : "gray"}
                      size="sm"
                    >
                      {budget.isActive
                        ? t("app.common.active")
                        : t("app.common.inactive")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2} justify="flex-end">
                      <Button
                        intent="ghost"
                        size="sm"
                        onClick={() => openEdit(budget)}
                      >
                        {t("app.common.edit")}
                      </Button>
                      <DeleteConfirmPopover
                        loading={deletingId === budget.id}
                        onConfirm={() => handleDelete(budget)}
                        title={t("app.aiGateway.budgets.deleteTitle")}
                        message={t("app.aiGateway.budgets.deleteMessage")}
                        triggerLabel={t("app.common.delete")}
                      />
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      <VStack align="stretch" gap={2}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.aiGateway.budgets.alerts.title")}
        </Text>
        {alerts.length === 0 ? (
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.aiGateway.budgets.alerts.empty")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm" variant="outline" minW="680px">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.budgets.alerts.budget")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.budgets.alerts.threshold")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.budgets.alerts.spend")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.budgets.alerts.triggeredAt")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {alerts.map((alert) => (
                  <Table.Row key={alert.id}>
                    <Table.Cell>{alert.budgetName}</Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette="red" size="sm">
                        {alert.thresholdPercent}%
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {t("app.aiGateway.budgets.alerts.spendValue", {
                        spend: formatUsd(alert.spendUsd),
                        amount: formatUsd(alert.budgetAmountUsd),
                      })}
                    </Table.Cell>
                    <Table.Cell>{formatDateTime(alert.triggeredAt)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>

      <BudgetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        budget={editing}
        widgets={widgets}
        customerCompanies={customerCompanies}
        onSaved={reload}
      />
    </VStack>
  );
};

export default BudgetsPanel;
